<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { countFillableFields, pageOrientation } from '@record-fill';
  import { Loader2 } from 'lucide-vue-next';
  import { Chip } from '@/shared/ui';
  import { useCustomToast, setBreadcrumbLabel, clearBreadcrumbLabel } from '@/shared/lib';
  import {
    matrixModelApi,
    FormBuilderViewerStage,
    FormBuilderFooter,
    useFormBuilderStore,
    type MatrixModel,
    type FormStructure,
  } from '@/features/FormBuilder';

  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();
  const formBuilderStore = useFormBuilderStore();

  const model = ref<MatrixModel | null>(null);
  const isLoading = ref(true);
  const hasError = ref(false);

  const zoom = ref(1);
  const pageCount = ref(1);
  const currentPage = ref(1);
  const viewScrollTop = ref(0);

  const scrollRef = ref<HTMLElement | null>(null);
  const canvasWrapperRef = ref<HTMLElement | null>(null);
  const canvasOffsetTop = ref<number | null>(null);

  const modelId = computed(() => Number(route.params.id));

  const totalFields = computed(() => countFillableFields(formBuilderStore.elements));

  const viewerStructure = computed<FormStructure | null>(() => {
    if (!model.value) return null;
    return {
      elements: model.value.structure.elements,
      version: model.value.structure.version,
      metadata: model.value.structure.metadata,
    };
  });
  const orientation = computed(() => pageOrientation(viewerStructure.value));

  function handleScroll(): void {
    const scrollEl = scrollRef.value;
    const canvasEl = canvasWrapperRef.value;
    if (!scrollEl || !canvasEl) return;
    if (canvasOffsetTop.value === null) {
      canvasOffsetTop.value =
        canvasEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
    }
    viewScrollTop.value = Math.max(0, scrollEl.scrollTop - canvasOffsetTop.value);
  }

  async function fetchModel(): Promise<void> {
    isLoading.value = true;
    hasError.value = false;
    try {
      const result = await matrixModelApi.getById(modelId.value);
      model.value = result;
      setBreadcrumbLabel(result.title);
    } catch (error) {
      console.error('Error fetching matrix model:', error);
      hasError.value = true;
      showErrorToast({
        title: 'Erro ao carregar modelo',
        type: 'error',
        hideIcon: true,
      });
      router.push({ name: 'GestaoRegistrosModelos' });
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(fetchModel);

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center"
      data-testid="view-model-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <template v-else-if="model && viewerStructure">
      <div
        ref="scrollRef"
        class="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] px-8 pt-6 pb-6"
        data-testid="view-model-scroll"
        @scroll="handleScroll"
      >
        <h1 class="text-2xl font-bold text-[var(--color-gray-07)]">
          {{ model.title }}
        </h1>

        <div
          v-if="model.categories.length"
          class="mt-3 flex flex-wrap items-center gap-2"
        >
          <Chip
            v-for="category in model.categories"
            :key="category.id"
            :label="category.name"
            :removable="false"
          />
        </div>

        <div
          ref="canvasWrapperRef"
          class="mt-6"
        >
          <FormBuilderViewerStage
            :structure="viewerStructure"
            :zoom="zoom"
            :view-scroll-top="viewScrollTop"
            @update:zoom="zoom = $event"
            @update:page-count="pageCount = $event"
            @update:current-page="currentPage = $event"
          />
        </div>
      </div>

      <FormBuilderFooter
        class="shrink-0"
        mode="view"
        :orientation="orientation"
        :zoom="zoom"
        :page-count="pageCount"
        :current-page="currentPage"
        :total-fields="totalFields"
        @update:zoom="zoom = $event"
      />
    </template>
  </div>
</template>
